import { XsI18nPipe } from '@nx-fe/pipes';
import { hd } from '@nx-fe/services';
import { MathHelpers } from '../../remote-control-ng/utils/math';
import bigInt from 'big-integer';
import { TaskManagerProxyService } from '../../remote-control-ng/services/taskManagerProxyService';
import { TaskMemoryUsage, TaskModule, TaskPackage, TaskThread } from '../../remote-control-ng/definitions';
import { TaskWindowsTimes } from 'app/modules/remote-control-ng/messages/pocket-com';

type IRcTask = hd.IRcTask;
type IProcessTimesList = hd.IProcessTimesList;
type IDropdownList = hd.IDropdownList;

export class TaskManagerState {
  public refreshSpeed = 'manual';
  public refreshSpeedValue = -1;
  public taskList: IRcTask[] = [];
  public taskManagerSort: hd.ISortInfo = {
    column: 'name',
    reverse: false,
  } as hd.ISortInfo;
  public windowsThreadTableSort: hd.ISortInfo = {
    column: 'threadId',
    reverse: false,
  } as hd.ISortInfo;
  public windowsModuleTableSort: hd.ISortInfo = {
    column: 'name',
    reverse: false,
  } as hd.ISortInfo;
  public androidPackageTableSort: hd.ISortInfo = {
    column: 'applications',
    reverse: false,
  } as hd.ISortInfo;
  public sortInfos: hd.ISortInfo[] = [];
  public windowsTaskThreads: hd.IWindowsProcessThread[];
  public windowsTaskModules: hd.IWindowsProcessModule[];
  public windowsTaskDetails: hd.IWindowsTaskDetails;
  public selectedTask: IRcTask;
  public inDetailsView = false;
  public androidProcessDetails: hd.IAndroidProcessDetails;
  private androidProcessMemory: TaskMemoryUsage;
  private androidProcessErrorInfo: hd.IAndroidProcessErrorStateInfo;
  private androidProcessApplication: hd.IAndroidProcessApplications[];
  protected initialized = false;
  private scrollPos: number;
  private transferState: TaskManagerState = null;
  private refreshCount = 0;
  private processTimesList: IProcessTimesList = {};
  private lastRefresh = 0;
  private lastDelta = 0;
  private decimalPlacesMultiplier = 10;
  private osType: string;

  public refreshSpeeds: IDropdownList;
  private currentThreadTimesReqId: number[] = [];
  private firstThreadRefresh = true;

  public isKillPermission: boolean;
  
  constructor(private saI18nPipe: XsI18nPipe, private taskManager: TaskManagerProxyService) {
    this.sortInfos[hd.TaskManagerTables.taskList] = this.taskManagerSort;
    this.sortInfos[hd.TaskManagerTables.windowsThreadTable] = this.windowsThreadTableSort;
    this.sortInfos[hd.TaskManagerTables.windowsModuleTable] = this.windowsModuleTableSort;
    this.sortInfos[hd.TaskManagerTables.androidPackageTable] = this.androidPackageTableSort;

    this.refreshSpeeds = {
      manual: {
        id: 'manual',
        caption: this.saI18nPipe.transform('taskManager_refresh_manual'),
        speed: -1,
      },
      slow: {
        id: 'slow',
        caption: this.saI18nPipe.transform('taskManager_refresh_slow'),
        speed: 3000,
      },
      normal: {
        id: 'normal',
        caption: this.saI18nPipe.transform('taskManager_refresh_normal'),
        speed: 1500,
      },
      fast: {
        id: 'fast',
        caption: this.saI18nPipe.transform('taskManager_refresh_fast'),
        speed: 750,
      },
    };

    this.selectedTask = null;
  }

  /** Selectively clones this state for the purpose of transferring */
  public static cloneForTransfer = (oldState: TaskManagerState, saI18nPipe: XsI18nPipe, taskManager: TaskManagerProxyService): TaskManagerState => {
    const newState = new TaskManagerState(saI18nPipe, taskManager);
    newState.processTimesList = oldState.processTimesList;
    newState.taskList = oldState.taskList;
    newState.windowsTaskThreads = oldState.windowsTaskThreads;
    newState.windowsTaskModules = oldState.windowsTaskModules;
    newState.inDetailsView = oldState.inDetailsView;
    newState.androidProcessDetails = oldState.androidProcessDetails;
    newState.scrollPos = oldState.scrollPos;
    newState.selectedTask = oldState.selectedTask;
    newState.sortInfos = oldState.sortInfos;
    newState.initialized = oldState.initialized;
    newState.osType = oldState.osType;
    newState.lastRefresh = oldState.lastRefresh;
    newState.refreshSpeed = oldState.refreshSpeed;
    newState.refreshSpeedValue = oldState.refreshSpeedValue;
    newState.isKillPermission = oldState.isKillPermission;

    return newState;
  };

  public init = () => {
    if (this.isWindows()) {
      this.taskManager.setThreadTimesWindowsCallback(this.threadProcessesTimesCallback);
    }

    if (!this.initialized) {
      this.refreshProcesses();
    }

    if (this.isWindows()) {
      this.refreshProcessesTimes();
    }
  };

  public getTaskList = (): IRcTask[] => this.taskList;

  public getTaskDetails = (pid: number) => {
    this.androidProcessDetails = undefined;
    this.taskManager.getTaskDetails(this.isWindows(), pid, (dataTable1, dataTable2, dataTable3) => {
      if (this.isWindows()) {
        const dataThread: TaskThread[] = <any>dataTable1 as TaskThread[];
        const dataModule: TaskModule[] = <any>dataTable2 as TaskModule[];
        this.windowsTaskThreads = new Array<hd.IWindowsProcessThread>(dataThread.length);
        this.windowsTaskModules = new Array<hd.IWindowsProcessModule>(dataModule.length);;
        for (let i = 0; i < dataThread.length; i++) {
          this.windowsTaskThreads[i] = {
            threadId: this.decimalToHexString(dataThread[i].tid),
            priority: dataThread[i].pri
          } as hd.IWindowsProcessThread;
        }
        for (let i = 0; i < dataModule.length; i++) {
          this.windowsTaskModules[i] = {
            name: dataModule[i].name,
            baseAddress: this.decimalToHexString(dataModule[i].baseAdd),
            baseSize: dataModule[i].baseSize,
            usage: this.decimalToHexString(dataModule[i].usage),
            procUsage: this.decimalToHexString(dataModule[i].procUsage),
            handle: this.decimalToHexString(dataModule[i].handle)
          } as hd.IWindowsProcessModule;
        }
        this.windowsTaskThreads.sort(
          this.sort(
            this.sortInfos[hd.TaskManagerTables.windowsThreadTable].column,
            this.sortInfos[hd.TaskManagerTables.windowsThreadTable].reverse
          )
        );
        this.windowsTaskModules.sort(
          this.sort(
            this.sortInfos[hd.TaskManagerTables.windowsModuleTable].column,
            this.sortInfos[hd.TaskManagerTables.windowsModuleTable].reverse
          )
        );
      } else {
        // table callback contains: jsonArrayPackages, jsonMemory, jsonErrorInfo
        this.androidProcessApplication = <any>dataTable1 as TaskPackage[];
        this.androidProcessMemory = dataTable2;
        this.androidProcessErrorInfo = dataTable3;

        this.androidProcessDetails = {
          applications: this.androidProcessApplication,
          memoryUsage: {
            // The value that Agent sends back is in KB already, but we are doing calculation based on bytes so cover it
            dalvikVM: MathHelpers.toSizeString(
              bigInt(this.androidProcessMemory.dalvikVM * 1024)
            ),
            native: MathHelpers.toSizeString(
              bigInt(this.androidProcessMemory.native * 1024)
            ),
            other: MathHelpers.toSizeString(
              bigInt(this.androidProcessMemory.other * 1024)
            ),
          },
          errorStateInfo: this.androidProcessErrorInfo,
        };
        this.androidProcessDetails.applications.sort(
          this.sort(
            this.sortInfos[hd.TaskManagerTables.androidPackageTable].column,
            this.sortInfos[hd.TaskManagerTables.androidPackageTable].reverse
          )
        );
      }
    });
  };

  public decimalToHexString = (num: number): any => {
    if (num < 0) {
      num = 0xffffffff + num + 1;
    }

    const str = num.toString(16).toLocaleUpperCase();
    const zeroStr = '00000000';
    return '0x' + zeroStr.slice(str.length) + str;
  };

  public sortTableBy = (column: string, listToBeSort: any, sortInfo: hd.ISortInfo) => {
    if (column === sortInfo.column) {
      sortInfo.reverse = !sortInfo.reverse;
    } else {
      sortInfo.reverse = false;
      sortInfo.column = column;
    }

    if (listToBeSort) {
      listToBeSort.sort(this.sort(sortInfo.column, sortInfo.reverse));
    }
  };

  public setRefreshSpeed = (value: string): void => {
    this.refreshSpeed = value;
    if (this.refreshSpeedValue === -1) {
      this.refreshSpeedValue = this.refreshSpeeds[value].speed;
      if (this.refreshSpeedValue > 0) {
        if (this.isWindows()) {
          setTimeout(this.refreshProcessesTimes, this.refreshSpeedValue);
        } else {
          setTimeout(this.refreshProcesses, this.refreshSpeedValue);
        }
      }
    } else {
      this.refreshSpeedValue = this.refreshSpeeds[value].speed;
    }
  };

  public manualRefreshProcesses = () => {
    if (this.refreshSpeedValue < 0) {
      this.refreshProcesses();
    }
  };

  public isManualRefreshDisabled = () => this.refreshSpeedValue > 0;

  public refreshProcesses = () => {
    this.taskManager.getTaskList(this.isWindows(), data => {
      this.initialized = true;
      if (this.isWindows()) {
        for (const item of data) {
          const task = this.taskList.find(task => task.pid === item.pid);
          if (task) {
            task.type =
              item.type === '' ? `[${this.saI18nPipe.transform('taskManager_background_process')}]` : item.type;
            task.name = item.name;
          } else {
            this.taskList.push({
              pid: item.pid,
              uid: item.uid,
              name: item.name,
              type: (item.type === ''
                ? '[' + this.saI18nPipe.transform('taskManager_background_process') + ']'
                : item.type),
              cpuUsage: 0
            } as hd.IRcTask);
          }
        }
        // Remove unfound processes
        this.taskList = this.taskList.filter((tel) => data.findIndex((del) => del.pid === tel.pid) > -1);
        if (this.selectedTask) {
          const index = this.taskList.findIndex((el) => el.pid === this.selectedTask.pid);
          if (index === -1) {
            this.toggleSelectedTask(this.selectedTask, index, 'taskManagerItem_' + index);
          }
        }
      } else {
        for (const item of data) {
          const task = this.taskList.find(task => task.pid === item.pid);
          if (task) {
            task.memory = item.memory;
            task.memoryStr = MathHelpers.toSizeString(bigInt(item.memory));
          } else {
            this.taskList.push({
              pid: item.pid,
              uid: item.uid,
              name: item.name,
              memory: item.memory,
              memoryStr: MathHelpers.toSizeString(bigInt(item.memory)),
            } as hd.IRcTask);
          }
        }
        // Remove unfound processes
        this.taskList = this.taskList.filter((tel) => data.findIndex((del) => del.pid === tel.pid) > -1);
      }

      if (this.isWindows() && this.refreshSpeedValue < 0) {
        this.refreshProcessesTimes();
      }

      this.taskList.sort(
        this.sort(
          this.sortInfos[hd.TaskManagerTables.taskList].column,
          this.sortInfos[hd.TaskManagerTables.taskList].reverse
        )
      );

      if (!this.isWindows()) {
        if (this.refreshSpeedValue > 0) {
          setTimeout(this.refreshProcesses, this.refreshSpeedValue);
        }
      }
    });
  };

  public refreshProcessesTimes = () => {
    this.currentThreadTimesReqId.push(this.taskManager.getThreadTimesList());
  };

  public threadProcessesTimesCallback = (data: Map<number, Map<number, TaskWindowsTimes>>, reqId: number) => {
    if (!this.initialized) {
      setTimeout(() => this.threadProcessesTimesCallback(data, reqId));
      return;
    }

    if (this.currentThreadTimesReqId.lastIndexOf(reqId) === -1) {
      return;
    } else {
      this.currentThreadTimesReqId = this.currentThreadTimesReqId.filter(v => v !== reqId);
    }

    // Get current milliseconds
    const now = new Date().getTime();
    if (this.isWindows()) {
      for (const [pid, processTasks] of data) {
        if (!this.processTimesList[pid]) {
          this.processTimesList[pid] = {} as hd.IProcessTimes;
        }
        for (const [tid, times] of processTasks) {
          if (times) {
            if (!this.processTimesList[pid][tid]) {
              // New process
              this.processTimesList[pid][tid] = {} as hd.IProcessTimeDelta;
            }
            if (this.processTimesList[pid][tid].now) {
              // Old process, swap the new time to the old time
              this.processTimesList[pid][tid].prev = this.processTimesList[pid][tid].now;
            } else {
              // New process, set old and new to the same value
              this.processTimesList[pid][tid].prev = times.kernel + times.user;
            }

            // Add amount of kernel time and user time together (we don't care about them seperately)
            this.processTimesList[pid][tid].now = times.kernel + times.user;

            // Calculate the difference between the last amount of cpu milliseconds used and
            // the current amount.
            this.processTimesList[pid][tid].delta =
              this.processTimesList[pid][tid].now - this.processTimesList[pid][tid].prev;
          }
        }
      }

      this.lastDelta = now - this.lastRefresh;
      const minDelta = this.refreshSpeedValue < 0 ? 500 : this.refreshSpeedValue;
      if (this.lastDelta < minDelta) {
        this.lastDelta = minDelta;
      }

      let refreshProcessesNeeded = false;
      for (const [pid, processTasks] of data) {
        if (this.taskList.findIndex((el) => el.pid === pid) === -1) {
          // We cannot find a pid in our list.  Must mean we are missing a process in our list.
          // Force a refresh
          this.taskList.push({ pid } as hd.IRcTask);
          refreshProcessesNeeded = true;
        }
      }

      if (refreshProcessesNeeded) {
        setTimeout(this.refreshProcesses, 0);
      }

      for (let i = 0; i < this.taskList.length; i++) {
        if (this.processTimesList[this.taskList[i].pid]) {
          const threadTimes = this.processTimesList[this.taskList[i].pid];
          let totalDelta = 0;
          for (const tid in threadTimes) {
            if (threadTimes.hasOwnProperty(tid)) {
              totalDelta += threadTimes[tid].delta;
            }
          }

          this.taskList[i].deltaTime = totalDelta;
          if (this.lastRefresh === 0) {
            this.taskList[i].cpuUsage = 0;
          } else {
            // Use the delta time (amount of cpu time allocated since the last time we checked the process)
            // divided by the delta of the current time and last refresh time to calculate the approximate cpu usage
            // eslint-disable-next-line no-bitwise
            const cpuUsage =
              (((totalDelta * (100 * this.decimalPlacesMultiplier)) / this.lastDelta) | 0) /
              this.decimalPlacesMultiplier;
            this.taskList[i].cpuUsage = Math.min(cpuUsage, 100);
          }
          this.taskList[i].cpuUsageStr = this.taskList[i].cpuUsage + '%';
        }

        if (!data.get(this.taskList[i].pid)) {
          // if we cannot find the current pid in the most recent data remove
          // it from the list, since the process no longer exists
          if (this.isTaskActionAllowed(this.taskList[i].pid)) {
            this.toggleSelectedTask(this.taskList[i], i, 'taskManagerItem_' + i);
          }
          this.processTimesList[this.taskList[i].pid] = {};
          this.taskList[i].pid = -1;
        }
      }

      // Filter out deleted processes
      this.taskList = this.taskList.filter(item => item.pid !== -1);

      this.lastRefresh = now;
      this.refreshCount++;
      // Sort by pid first.  This keeps processes from jumping around when the sort column values are all equal.
      this.taskList.sort(this.sort('pid', this.sortInfos[hd.TaskManagerTables.taskList].reverse));
      this.taskList.sort(
        this.sort(
          this.sortInfos[hd.TaskManagerTables.taskList].column,
          this.sortInfos[hd.TaskManagerTables.taskList].reverse
        )
      );

      if (this.refreshSpeedValue > 0) {
        if (!this.firstThreadRefresh) {
          setTimeout(this.refreshProcessesTimes, this.refreshSpeedValue);
        }
      } else if (this.firstThreadRefresh) {
        setTimeout(this.refreshProcessesTimes, 500);
      }

      this.firstThreadRefresh = false;
    }
  };

  public transfer = (oldState: TaskManagerState) => {
    if (oldState.initialized) {
      // Need to clone the old state instead of simply reference it, since it may be used after its owner window
      // is destroyed (which can cause issues in IE and Edge)
      this.transferState = TaskManagerState.cloneForTransfer(oldState, this.saI18nPipe, this.taskManager);

      this.scrollPos = this.transferState.getScrollValue();

      this.transferStateContents();

      // Re-initialize if neccessary (in the case where we are transferring into an existing file explorer)
      this.initialized = false;
      this.init();
      oldState.firstThreadRefresh = true;
    }
  };

  public transferStateContents = () => {
    this.processTimesList = this.transferState.processTimesList;
    this.taskList = this.transferState.taskList;
    this.windowsTaskThreads = this.transferState.windowsTaskThreads;
    this.windowsTaskModules = this.transferState.windowsTaskModules;
    this.lastRefresh = this.transferState.lastRefresh;
    this.inDetailsView = this.transferState.inDetailsView;
    this.androidProcessDetails = this.transferState.androidProcessDetails;
    this.scrollPos = this.transferState.scrollPos;
    this.selectedTask = this.transferState.selectedTask;
    this.sortInfos = this.transferState.sortInfos;
    this.refreshSpeedValue = this.transferState.refreshSpeedValue;
    this.refreshSpeed = this.transferState.refreshSpeed;
    this.initialized = this.transferState.initialized;
    this.osType = this.transferState.osType;
    this.transferState = null;

    setTimeout(this.setTransferScrollPosition);
  };

  public setTransferScrollPosition = () => {
    setTimeout(() => {
      const taskManagerScrollArea = document.getElementById('taskManagerScrollArea');
      if (taskManagerScrollArea && taskManagerScrollArea.scrollHeight) {
        taskManagerScrollArea.scrollTop = this.scrollPos * taskManagerScrollArea.scrollHeight;
      }
    });
  };

  public canPopout = (): boolean => true;

  public getScrollValue = (): number => this.scrollPos;

  public onScroll = () => {
    const taskManagerScrollArea = document.getElementById('taskManagerScrollArea');
    const taskManagerHeadings = document.getElementById('taskManagerHeadings');
    taskManagerHeadings.scrollLeft = taskManagerScrollArea.scrollLeft;

    this.updateScrollPos();
  };

  public updateScrollPos = () => {
    const taskManagerScrollArea = document.getElementById('taskManagerScrollArea');
    const pos = taskManagerScrollArea.scrollTop;
    const height = taskManagerScrollArea.scrollHeight;

    this.scrollPos = pos / height;
  };

  public setOs(os: string) {
    this.osType = os;
  }

  public getOs(): string {
    return this.osType;
  }

  public toggleSelectedTask(task: IRcTask, index: number, id: string) {
    const element = document.getElementById(id);
    const wasTaskSelected = this.selectedTask != null;
    if (!this.inDetailsView) {
      if (this.isTaskActionAllowed(task.pid)) {
        this.selectedTask = null;
      } else {
        this.selectedTask = task;
        const lines = 3; // number of lines at risk of being hidden
        if (!wasTaskSelected && this.isScrollNeeded(element, index, lines)) {
          setTimeout(() => {
            element.scrollIntoView(false);
          }, 100);
        }
      }
    }
  }

  /** the task can be killed when selected and the user has the permission */
  public isTaskActionAllowed(pid: number) {
    return this.selectedTask !== null && pid === this.selectedTask.pid && this.isKillPermission;
  }

  public isTaskSelected() {
    return this.selectedTask !== null && this.taskList.indexOf(this.selectedTask) !== -1;
  }

  public sendKillProcess = () => {
    if (this.isWindows()) {
      this.taskManager.sendKillWindowsProcess(this.selectedTask.pid);
      this.manualRefreshProcesses();
    }
  };

  private isScrollNeeded = (element: Element, index: number, lines: number): boolean => {
    const taskManagerScrollArea = document.getElementById('taskManagerScrollArea');
    const scrollExtent = taskManagerScrollArea.scrollTop + taskManagerScrollArea.clientHeight;
    // const borderString = $(element).css('border-bottom-width');
    const border = 4; // parseInt(borderString, 10);
    return (index + lines) * (element.clientHeight + border) > scrollExtent;
  };

  /**
   * This does not mean only windows OS, it refers to the way we handle the taskmanager
   * Options are android like vs windows like
   */
  public isWindows = (): boolean =>
    this.osType === 'WindowsCE' ||
    this.osType === 'WindowsDesktopLegacy' ||
    this.osType === 'WindowsDesktop' ||
    this.osType === 'Linux' ||
    this.osType === 'Tizen' ||
    this.osType === 'Mac';

  public sort(sortColumn: string, reverse: boolean) {
    const sortReverse = !reverse ? 1 : -1;

    return (a: any, b: any) => {
      let aa = a[sortColumn];
      let bb = b[sortColumn];
      if (typeof aa === 'string' && typeof bb === 'string') {
        aa = aa.toLocaleLowerCase();
        bb = bb.toLocaleLowerCase();
      }
      if (aa > bb) {
        return sortReverse;
      } else if (bb > aa) {
        return -sortReverse;
      } else {
        return 0;
      }
    };
  }
}
